import { getKpiValueKey } from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import { normalizeValueForInput } from "@core/reports/utils/fieldTypeUtils";
import type {
    ActivityEntryPrefillCandidate,
    ActivityEntryPrefillEntry,
} from "@features/report/activity-entry/types/activityEntry";
import type { ActivityKpiContext } from "@features/report/activity-entry/utils/activityEntryGroupHelpers";

/**
 * Map server Prefill entries into the activity entry form values map.
 * Trusts server sanitization; only keys slots and normalizes for inputs.
 */
export function mapPrefillEntriesToFormValues(
    entries: ActivityEntryPrefillEntry[],
    kpiContexts: ActivityKpiContext[]
): Record<string, string> {
    const dataTypeBySlot = new Map<string, string>();
    for (const { subGroupId, activity, kpi } of kpiContexts) {
        const key = getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id);
        dataTypeBySlot.set(key, kpi.data_type?.data_type_name ?? "");
    }

    const values: Record<string, string> = {};
    for (const entry of entries) {
        const key = getKpiValueKey(
            entry.report_sub_group_id ?? null,
            entry.activity_id,
            entry.kpi_id
        );
        if (!dataTypeBySlot.has(key)) {
            continue;
        }
        values[key] = normalizeValueForInput(
            entry.kpi_value,
            dataTypeBySlot.get(key) ?? ""
        );
    }
    return values;
}

/**
 * Default candidate: nearest prior by since < targetSince; else first (newest).
 */
export function pickDefaultPrefillCandidate(
    candidates: ActivityEntryPrefillCandidate[],
    targetSince: string | null | undefined
): number | null {
    if (candidates.length === 0) return null;

    const target = (targetSince ?? "").toString().slice(0, 10);
    if (target) {
        const prior = candidates.find((c) => {
            const since = (c.since ?? "").toString().slice(0, 10);
            return since !== "" && since < target;
        });
        if (prior) return prior.report_tag_id;
    }

    return candidates[0].report_tag_id;
}

export function formatPrefillCandidateLabel(
    candidate: ActivityEntryPrefillCandidate
): string {
    const since = (candidate.since ?? "").toString().slice(0, 10);
    const until = (candidate.until ?? "").toString().slice(0, 10);
    if (since && until) {
        return `${candidate.name} (${since} – ${until})`;
    }
    if (since || until) {
        return `${candidate.name} (${since || until})`;
    }
    return candidate.name;
}
